// 用户消息组件

import { motion } from "framer-motion";
import { memo, type ReactNode } from "react";
import type { Attachment } from "@/types/chat";
import { AttachmentPreview } from "@/components/chatbottom/AttachmentPreview";
import { QuotePreview } from "@/components/chatbottom/QuotePreview";
import { useChatContext } from "@/components/chatview/ChatContext";
import { inferContentTypeFromFileName, isImageFile } from "@/utils/imageFile";

interface UserMessageProps {
  content: string | ReactNode;
  attachments?: Array<Attachment | string>;
  quote?: string;
}

export const UserMessage = memo(function UserMessage({
  content,
  attachments,
  ...props
}: UserMessageProps) {
  const { previewFile } = useChatContext();
  const normalizedAttachments = attachments?.map((item, index) => {
    if (typeof item === "object" && item !== null && "url" in item) {
      return item;
    }

    if (typeof item === "string") {
      const fileName = item.split("/").pop() || `file-${index}`;
      const contentType = isImageFile(fileName)
        ? inferContentTypeFromFileName(fileName) || "image/jpeg"
        : "";

      return {
        id: `attachment-${index}`,
        name: fileName,
        url: item,
        contentType,
        size: undefined,
      };
    }
    return item as Attachment;
  });

  return (
    <motion.div
      className="w-full flex flex-col gap-2 items-end"
      initial={{ y: 5, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
    >
      <div className="flex flex-col gap-2 items-end w-full">
        {/* 引用内容 */}
        {props.quote && (
          <div className="max-w-[80%]">
            <QuotePreview
              quote={props.quote}
              className="rounded-lg border-none bg-gray-50 text-gray-500"
            />
          </div>
        )}
        {normalizedAttachments && normalizedAttachments.length > 0 && (
          <div className="flex flex-row gap-2 justify-end">
            <div className=" p-2 rounded-lg">
              <div className="text-sm">
                <AttachmentPreview
                  attachments={normalizedAttachments}
                  showRemoveButton={false}
                  onPreview={(attachment, fileUrl) => {
                    previewFile?.({
                      url: fileUrl || attachment.url,
                      name: attachment.name,
                      contentType: attachment.contentType,
                    });
                  }}
                />
              </div>
            </div>
          </div>
        )}
        {/* 用户消息内容 */}
        {content && (
          <div className="max-w-[80%] flex flex-col gap-4 bg-[var(--user-message-bg)] text-[var(--user-message-text)] py-2.5 px-4 rounded-[var(--user-message-radius)]">
            {typeof content === "string" ? (
              <p className="whitespace-pre-wrap text-base break-words">
                {content}
              </p>
            ) : (
              content
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
});
